// Reachability derivation for the code-knowledge index (issue #353). Turns the raw
// edges into the two signals both downstream features need: how many distinct
// NON-test files reference a file/symbol (`caller_count`, the reuse signal), and
// whether something has no such callers and is not an entry point (`orphan`, the
// dead-code signal). Test callers are excluded so a symbol used only by its own test
// still reads as unused in production — the truthful basis for AC-2.

import { isTestFile } from './entry-points.js';
import type {
  CodeKnowledgeFile,
  CodeKnowledgeImportEdge,
  CodeKnowledgeReferenceEdge,
} from './types.js';

/** Stable key for a symbol's caller count: its defining file + name. */
export function symbolKey(file: string, name: string): string {
  return `${file} ${name}`;
}

export interface ReachabilityInput {
  /** Every indexed file (project-relative, forward-slash). */
  files: string[];
  importEdges: CodeKnowledgeImportEdge[];
  referenceEdges: CodeKnowledgeReferenceEdge[];
  /** The entry-point allowlist — a file in it is never flagged orphan. */
  entryFiles: Set<string>;
}

export interface ReachabilityResult {
  files: CodeKnowledgeFile[];
  /** Distinct non-test caller count per symbol, keyed by {@link symbolKey}. */
  symbolCallerCount: Map<string, number>;
}

/** Count distinct non-test callers per target across a set of edges. */
function countCallers<T extends { from: string }>(
  edges: T[],
  keyOf: (edge: T) => string,
): Map<string, Set<string>> {
  const callers = new Map<string, Set<string>>();
  for (const edge of edges) {
    if (isTestFile(edge.from)) continue;
    const key = keyOf(edge);
    let set = callers.get(key);
    if (!set) {
      set = new Set<string>();
      callers.set(key, set);
    }
    set.add(edge.from);
  }
  return callers;
}

export function computeReachability(input: ReachabilityInput): ReachabilityResult {
  const fileCallers = countCallers(input.importEdges, (edge) => edge.to);
  const files: CodeKnowledgeFile[] = input.files.map((path) => {
    const callerCount = fileCallers.get(path)?.size ?? 0;
    const entryPoint = input.entryFiles.has(path);
    return {
      path,
      caller_count: callerCount,
      orphan: callerCount === 0 && !entryPoint,
      entry_point: entryPoint,
    };
  });

  const symbolCallers = countCallers(input.referenceEdges, (edge) =>
    symbolKey(edge.to, edge.symbol),
  );
  const symbolCallerCount = new Map<string, number>();
  for (const [key, set] of symbolCallers) {
    symbolCallerCount.set(key, set.size);
  }

  return { files, symbolCallerCount };
}
